import React from 'react';

import {Input, Label, Text} from '@albert-io/atomic';
import {
  CheckboxFilterSection,
  FiltersMenuPanel,
  FiltersMenuShell
} from 'client/TeacherDashboard/components/FiltersMenu';
import {
  ASSIGNMENT_TYPE_CHECKBOX_OPTIONS,
  CREATED_BY_CHECKBOX_OPTIONS,
  GRADING_STATE_CHECKBOX_OPTIONS
} from 'client/TeacherDashboard/config/filterOptions';
import type {
  AssignmentTypeFilter,
  ClassworkCreatedByFilter,
  ClassworkGradingStateFilter
} from 'client/TeacherDashboard/types';

interface Props {
  createdBy: ReadonlyArray<ClassworkCreatedByFilter>;
  assignmentType: ReadonlyArray<AssignmentTypeFilter>;
  /** Due-date range bounds as YYYY-MM-DD (native date-input format); '' = unset. */
  dueDateStart: string;
  dueDateEnd: string;
  gradingState: ReadonlyArray<ClassworkGradingStateFilter>;
  /**
   * `frq_teacher_grading` is on, so the rows carry grade/release actions and grading progress is
   * something the teacher can act on. Off, the section is dropped rather than shown inert.
   */
  isFrqGradingEnabled: boolean;
  disabled?: boolean;
  onChange: (value: ReadonlyArray<ClassworkCreatedByFilter>) => void;
  onAssignmentTypeChange: (value: ReadonlyArray<AssignmentTypeFilter>) => void;
  onGradingStateChange: (value: ReadonlyArray<ClassworkGradingStateFilter>) => void;
  onDueDateChange: (bound: 'start' | 'end', value: string) => void;
}

export const FiltersMenu = ({
  createdBy,
  assignmentType,
  dueDateStart,
  dueDateEnd,
  gradingState,
  isFrqGradingEnabled,
  disabled,
  onChange,
  onAssignmentTypeChange,
  onGradingStateChange,
  onDueDateChange
}: Props) => {
  const isActive =
    createdBy.length > 0 ||
    assignmentType.length > 0 ||
    (isFrqGradingEnabled && gradingState.length > 0) ||
    !!dueDateStart ||
    !!dueDateEnd;
  return (
    <FiltersMenuShell data-testid='filters-trigger' disabled={disabled} isActive={isActive}>
      <FiltersMenuPanel>
        <CheckboxFilterSection
          label='Created By'
          options={CREATED_BY_CHECKBOX_OPTIONS}
          selected={createdBy}
          onChange={onChange}
        />
        <CheckboxFilterSection
          label='Type'
          options={ASSIGNMENT_TYPE_CHECKBOX_OPTIONS}
          selected={assignmentType}
          onChange={onAssignmentTypeChange}
        />
        {isFrqGradingEnabled ? (
          <CheckboxFilterSection
            label='Grading Status'
            options={GRADING_STATE_CHECKBOX_OPTIONS}
            selected={gradingState}
            onChange={onGradingStateChange}
          />
        ) : null}
        <div className='gap-x1-half flex flex-col'>
          <Text size='s'>Due Date</Text>
          <div className='gap-x2 flex'>
            <Label className='gap-x1 text-s flex flex-1 flex-col'>
              Start
              <Input
                className='rounded-x1'
                size='s'
                type='date'
                value={dueDateStart}
                max={dueDateEnd || undefined}
                onChange={(e) => onDueDateChange('start', e.target.value)}
              />
            </Label>
            <Label className='gap-x1 text-s flex flex-1 flex-col'>
              End
              <Input
                className='rounded-x1'
                size='s'
                type='date'
                value={dueDateEnd}
                min={dueDateStart || undefined}
                onChange={(e) => onDueDateChange('end', e.target.value)}
              />
            </Label>
          </div>
        </div>
      </FiltersMenuPanel>
    </FiltersMenuShell>
  );
};
